// DriftRuleBuilder.js — Custom drift rule creation form (Pro+ tier)
import React, { useState } from 'react';
import { createRule } from '../services/driftService';
import './DriftRuleBuilder.css';

const CONDITION_TYPES = [
    { value: 'port_open', label: 'Port Open', valueLabel: 'Port numbers (comma-separated)', valuePlaceholder: '22, 3389, 8080' },
    { value: 'finding_severity', label: 'Finding Severity', valueType: 'severity' },
    { value: 'finding_template', label: 'Finding Template', valueLabel: 'Template keywords (comma-separated)', valuePlaceholder: 'sql-injection, xss, rce' },
    { value: 'cert_expiring', label: 'Certificate Expiring', valueLabel: 'Days until expiry', valuePlaceholder: '30', valueType: 'number' },
    { value: 'cert_issuer_changed', label: 'Certificate Issuer Changed', noValue: true },
    { value: 'subdomain_new', label: 'New Subdomain', noValue: true },
    { value: 'any_port_open', label: 'Any New Port', noValue: true },
    { value: 'tech_version_changed', label: 'Technology Version Changed', noValue: true },
    { value: 'risk_score_threshold', label: 'Risk Score Threshold', valueLabel: 'Alert when score at or below', valuePlaceholder: '70', valueType: 'number' },
    { value: 'risk_score_delta', label: 'Risk Score Change', valueLabel: 'Point threshold', valuePlaceholder: '5', valueType: 'number', hasDirection: true },
    { value: 'cert_new_issued', label: 'New Certificate Issued', noValue: true },
    { value: 'cert_wildcard_issued', label: 'Wildcard Certificate', noValue: true },
    { value: 'dns_record_changed', label: 'DNS Record Changed', noValue: true },
    { value: 'dns_ns_changed', label: 'Nameserver Changed', noValue: true },
    { value: 'dns_mx_changed', label: 'MX Record Changed', noValue: true },
    { value: 'dns_txt_changed', label: 'TXT Record Changed', noValue: true },
    { value: 'dangling_cname', label: 'Dangling CNAME', noValue: true }
];

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'info'];

function DriftRuleBuilder({ onCreated, onCancel, assets }) {
    const [name, setName] = useState('');
    const [conditionType, setConditionType] = useState('port_open');
    const [conditionValue, setConditionValue] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [scope, setScope] = useState('all');
    const [scopeAssets, setScopeAssets] = useState([]);
    const [direction, setDirection] = useState('drop');
    const [alarmEnabled, setAlarmEnabled] = useState(true);
    const [emailEnabled, setEmailEnabled] = useState(false);
    const [emailCadence, setEmailCadence] = useState('digest');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const selectedCondition = CONDITION_TYPES.find(c => c.value === conditionType);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!name.trim()) {
            setError('Rule name is required');
            return;
        }

        try {
            setSaving(true);
            const finalConditionValue = selectedCondition?.hasDirection
                ? JSON.stringify({ threshold: parseInt(conditionValue) || 5, direction })
                : (selectedCondition?.noValue ? '' : conditionValue);

            const ruleData = {
                name: name.trim(),
                conditionType,
                conditionValue: finalConditionValue,
                severity,
                scope: scope === 'all' ? 'all' : scopeAssets.join(','),
                description: `Custom rule: ${name.trim()}`,
                alarmEnabled,
                emailEnabled,
                emailCadence
            };

            await createRule(ruleData);
            onCreated?.();
        } catch (err) {
            setError(err.message || 'Failed to create rule');
        } finally {
            setSaving(false);
        }
    };

    const handleScopeAssetToggle = (assetId) => {
        setScopeAssets(prev =>
            prev.includes(assetId)
                ? prev.filter(id => id !== assetId)
                : [...prev, assetId]
        );
    };

    return (
        <div className="drift-rule-builder">
            <h4 className="drift-rule-builder-title">Create Custom Rule</h4>

            {error && <div className="drift-rule-builder-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="drift-rule-builder-field">
                    <label>Rule Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Alert on port 8443"
                        maxLength={100}
                        disabled={saving}
                    />
                </div>

                <div className="drift-rule-builder-field">
                    <label>Condition Type</label>
                    <select
                        value={conditionType}
                        onChange={(e) => { setConditionType(e.target.value); setConditionValue(''); setDirection('drop'); }}
                        disabled={saving}
                    >
                        {CONDITION_TYPES.map(ct => (
                            <option key={ct.value} value={ct.value}>{ct.label}</option>
                        ))}
                    </select>
                </div>

                {!selectedCondition?.noValue && (
                    <div className="drift-rule-builder-field">
                        <label>{selectedCondition?.valueLabel || 'Value'}</label>
                        {selectedCondition?.valueType === 'severity' ? (
                            <select
                                value={conditionValue}
                                onChange={(e) => setConditionValue(e.target.value)}
                                disabled={saving}
                            >
                                <option value="">Select severity...</option>
                                {SEVERITY_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type={selectedCondition?.valueType === 'number' ? 'number' : 'text'}
                                value={conditionValue}
                                onChange={(e) => setConditionValue(e.target.value)}
                                placeholder={selectedCondition?.valuePlaceholder || ''}
                                disabled={saving}
                            />
                        )}
                    </div>
                )}

                {selectedCondition?.hasDirection && (
                    <div className="drift-rule-builder-field">
                        <label>Direction</label>
                        <select
                            value={direction}
                            onChange={(e) => setDirection(e.target.value)}
                            disabled={saving}
                        >
                            <option value="drop">Score Drops (worsening security)</option>
                            <option value="rise">Score Rises (improving security)</option>
                        </select>
                        <span className="drift-field-hint">
                            Higher scores = healthier. A "drop" means security is degrading.
                        </span>
                    </div>
                )}

                <div className="drift-rule-builder-field">
                    <label>Severity</label>
                    <div className="drift-rule-builder-severity-group">
                        {SEVERITY_OPTIONS.map(s => (
                            <label key={s} className={`drift-rule-builder-severity-option ${severity === s ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="severity"
                                    value={s}
                                    checked={severity === s}
                                    onChange={(e) => setSeverity(e.target.value)}
                                    disabled={saving}
                                />
                                <span className="drift-severity-dot" data-severity={s}></span>
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="drift-rule-builder-field">
                    <label>Alerting</label>
                    <div className="drift-rule-builder-alerting">
                        <label className="toggle-label">
                            <input
                                type="checkbox"
                                checked={alarmEnabled}
                                onChange={(e) => {
                                    const v = e.target.checked;
                                    setAlarmEnabled(v);
                                    if (!v) setEmailEnabled(false);
                                }}
                                disabled={saving}
                            />
                            <span className="drift-rule-control-text">Alarm</span>
                        </label>
                        <label className="toggle-label">
                            <input
                                type="checkbox"
                                checked={emailEnabled}
                                onChange={(e) => setEmailEnabled(e.target.checked)}
                                disabled={saving || !alarmEnabled}
                            />
                            <span className="drift-rule-control-text">Email</span>
                        </label>
                        {emailEnabled && (
                            <select
                                value={emailCadence}
                                onChange={(e) => setEmailCadence(e.target.value)}
                                disabled={saving}
                            >
                                <option value="instant">Instant</option>
                                <option value="digest">Digest</option>
                            </select>
                        )}
                    </div>
                    <span className="drift-field-hint">
                        Alarm shows matches in DriftAlarms; Email also notifies you. "Off" means don't alarm me about this.
                    </span>
                </div>

                <div className="drift-rule-builder-field">
                    <label>Scope</label>
                    <div className="drift-rule-builder-scope-group">
                        <label className="drift-rule-builder-scope-option">
                            <input
                                type="radio"
                                name="scope"
                                value="all"
                                checked={scope === 'all'}
                                onChange={() => setScope('all')}
                                disabled={saving}
                            />
                            All assets
                        </label>
                        <label className="drift-rule-builder-scope-option">
                            <input
                                type="radio"
                                name="scope"
                                value="specific"
                                checked={scope === 'specific'}
                                onChange={() => setScope('specific')}
                                disabled={saving}
                            />
                            Specific assets
                        </label>
                    </div>

                    {scope === 'specific' && assets && (
                        <div className="drift-rule-builder-asset-list">
                            {assets.map(a => {
                                const aid = a.assetId || a.rowKey;
                                return (
                                    <label key={aid} className="drift-rule-builder-asset-item">
                                        <input
                                            type="checkbox"
                                            checked={scopeAssets.includes(aid)}
                                            onChange={() => handleScopeAssetToggle(aid)}
                                            disabled={saving}
                                        />
                                        {a.target || a.name || aid}
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="drift-rule-builder-actions">
                    <button type="submit" className="cm-action-btn primary" disabled={saving}>
                        {saving ? 'Creating...' : 'Create Rule'}
                    </button>
                    <button type="button" className="cm-action-btn" onClick={onCancel} disabled={saving}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

export default DriftRuleBuilder;
